import { Alert, Button, Col, Form, Input, List, Row, Spin } from "antd";
import { buttonStyle, fullWidthStyle, titleStyle } from "../../constants/style";
import ApiContext from "../../api/context";
import { useContext, useEffect, useState } from "react";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { WorkOrder, WorkOrderStatus, WorkOrderStatusType } from "../../api/models";
import { toDateTimeString, toPrintableWorkOrderStatus } from "../../constants";

type WorkOrderStatusesProps = {
  workOrder?: WorkOrder,
  onClose: () => void,
  onSave: (workOrder: WorkOrder) => void
};

const POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUS_TYPES = [WorkOrderStatusType.Done, WorkOrderStatusType.Failed, WorkOrderStatusType.Archived];
const NON_LOADING_STATUS_TYPES = [WorkOrderStatusType.Archived, WorkOrderStatusType.Failed];

function sortByStatusTimeDescending(statuses: WorkOrderStatus[]): WorkOrderStatus[] {
  return [...statuses].sort((a, b) => new Date(b.statusTime).getTime() - new Date(a.statusTime).getTime());
}

export default function (p: WorkOrderStatusesProps) {
  const [statuses, setStatuses] = useState<WorkOrderStatus[]>([]);
  const [error, setError] = useState<string>();
  const { api } = useContext(ApiContext);
  const latestStatusType = statuses[0]?.workOrderStatusType;
  const isLoading = !error && (latestStatusType === undefined || !NON_LOADING_STATUS_TYPES.includes(latestStatusType));

  // Poll the work order's status list every 5 seconds until a terminal status is reached.
  useEffect(() => {
    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    function poll() {
      api?.searchWorkOrderStatuses(p.workOrder?.workOrderID!).then(response => {
        if (isCancelled) {
          return;
        }

        if (!response.good() || !response.body) {
          setError("Failed to get the work order statuses.");
          return;
        }

        const sortedStatuses = sortByStatusTimeDescending(response.body);
        setStatuses(sortedStatuses);
        setError(undefined);

        const latestStatusType = sortedStatuses[0]?.workOrderStatusType;
        const isTerminal = latestStatusType !== undefined && TERMINAL_STATUS_TYPES.includes(latestStatusType);
        if (!isTerminal) {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
          return;
        }

        if (latestStatusType !== WorkOrderStatusType.Done) {
          return;
        }

        api?.getWorkOrder(p.workOrder?.workOrderID!).then(workOrderResponse => {
          if (isCancelled || !workOrderResponse.good() || !workOrderResponse.body) {
            return;
          }

          p.onSave(workOrderResponse.body);
        });
      }).catch(() => {
        if (!isCancelled) {
          setError("Failed to get the work order statuses.");
        }
      });
    }

    poll();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      <Row justify="space-between" align="middle" style={fullWidthStyle}>
        <Title style={titleStyle} level={4}>Work Order Statuses</Title>
        <Spin size="small" spinning={isLoading} />
      </Row>
      <Form
        layout="vertical"
        colon={false}>
        <Form.Item label="Work Order Name">
          <Paragraph strong>{p.workOrder?.name}</Paragraph>
        </Form.Item>
        <Form.Item label="Work Order ID">
          <Paragraph strong>{p.workOrder?.workOrderID}</Paragraph>
        </Form.Item>
        <Form.Item label="Statuses">
          <List
            bordered={false}
            footer={false}
            pagination={{ pageSize: 5, hideOnSinglePage: statuses.length < 6 }}
            size="small"
            locale={{ emptyText: "There are no statuses to show at this time." }}
            dataSource={statuses}
            renderItem={(item: WorkOrderStatus) =>
              <List.Item key={`${item.workOrderStatusType}-${item.statusTime}`}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={fullWidthStyle}>
                  <span>{toPrintableWorkOrderStatus(item.workOrderStatusType)}</span>
                  <span>{toDateTimeString(item.statusTime)}</span>
                </Row>
              </List.Item>
            }
          >
          </List>
        </Form.Item>
        <Row style={fullWidthStyle} justify="end" align="middle">
          <Button
            type="default"
            style={buttonStyle}
            onClick={p.onClose}>
            Close
          </Button>
        </Row>
        {
          !error ? undefined :
            <>
              <Paragraph></Paragraph>
              <Alert style={fullWidthStyle} type="error" title={error} />
            </>
        }
      </Form>
    </>);
}
